/* global fbq */

import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

// Dynamically import all blog JSON metadata
function importPosts() {
  const context = require.context("../blog-posts", false, /\.json$/);
  return context.keys().map((key) => ({
    ...(context(key)),
    slug: key.replace(/^[.\\/]+/, "").replace(/\.json$/, ""),
  }));
}
const posts = importPosts().sort(
  (a, b) => new Date(b.date) - new Date(a.date)
);
const POSTS_PER_LOAD = 10;

export default function Blog() {
  const [visibleCount, setVisibleCount] = useState(POSTS_PER_LOAD);
  const hero = posts[0];
  const morePosts = posts.slice(1, visibleCount + 1);
  const hasMore = visibleCount + 1 < posts.length;

  function loadMore() {
    setVisibleCount((c) => c + POSTS_PER_LOAD);
  }

  
  const pushToDataLayer = (payload) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
  };


  function SubtlePromoAd() {
    return (
      <aside className="rounded-md bg-[var(--bg-surface-variant)] border border-[var(--border)] p-4 text-[var(--secondary-text)] text-center max-w-6xl mx-auto">
        <p>
          Need a{' '}
          <Link to="/get-boosting/division-boosting" className="text-[var(--blue)]" onClick={() => {
              if (typeof fbq !== 'undefined') {
                fbq('track', 'Lead', { content_name: 'Blog Site' });
              }
              pushToDataLayer({
                event: 'blog_cta_click',
                blog_title: 'Blog Site',
                cta_label: 'Boosting'
              });
          }}>
            boosting{' '}
          </Link>
           or{' '}
            <Link to="/get-coaching/mixed" className="text-[var(--blue)]" onClick={() => {
              if (typeof fbq !== 'undefined') {
                fbq('track', 'Lead', { content_name: 'Blog Site' });
              }
              pushToDataLayer({
                event: 'blog_cta_click',
                blog_title: 'Blog Site',
                cta_label: 'Coaching'
              });
          }}>
            coaching
          </Link>
          ? EloFactory's team of experts are here to help.
        </p>
      </aside>
    );
  }

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Gaming Blog | EloFactory.gg</title>
        <meta
          name="description"
          content="Stay updated with the latest League of Legends news, esports highlights, and boosting/coaching tips on our dark-themed gaming blog."
        />
        <link rel="canonical" href="https://elofactory.gg/blog" />
        {/* Open Graph & Twitter */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Gaming Blog | EloFactory.gg" />
        <meta
          property="og:description"
          content="Stay updated with the latest League of Legends news, esports highlights, and boosting/coaching tips on our dark-themed gaming blog."
        />
        <meta property="og:image" content="https://elofactory.gg/assets/og-blog.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <main className="bg-[var(--bg-page)] min-h-screen pb-8 pt-8 px-2 mt-8">
        {/* HERO */}
        <section className="relative mb-12 max-w-6xl mx-auto rounded-md overflow-hidden">
          <img
            src={require(`../assets/Blog/hero_splash.jpg`)}
            alt={hero.imageAlt || hero.title}
            className="absolute inset-0 w-full h-full object-cover opacity-50"
          />
          <div className="relative p-10 bg-gradient-to-t from-black/80 to-transparent">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              <Link to={`/blog/${hero.slug}`}>{hero.title}</Link>
            </h1>
            <p className="text-lg md:text-xl text-[var(--secondary-text)] max-w-prose">
              {hero.summary}
            </p>
            <Link
              to={`/blog/${hero.slug}`}
              className="mt-6 inline-block text-[var(--blue)] underline font-semibold"
            >
              Read more
            </Link>
          </div>
        </section>

        {/* POSTS + SUBTLE PROMO AFTER 2ND POST */}
        <div className="flex flex-col gap-10 max-w-6xl mx-auto mt-8">
          {morePosts.map((post, idx) => (
            <React.Fragment key={post.slug}>
              <article className="rounded-md bg-[var(--bg-surface)] border border-[var(--border)] p-8 shadow-lg transition-shadow hover:shadow-2xl">
                <div className="flex flex-col md:flex-row gap-6">
                  <Link to={`/blog/${post.slug}`} className="md:w-72 flex-shrink-0 rounded-md overflow-hidden">
                    <img
                      src={require(`../assets/Blog/${post.image}`)}
                      alt={post.imageAlt || post.title}
                      className="w-full h-full object-cover aspect-video"
                      loading="lazy"
                    />
                  </Link>
                  <div className="flex-1 flex flex-col justify-between">
                    <header>
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                      </h3>
                      <time className="block text-xs md:text-sm text-[var(--secondary-text)] mb-3">
                        {new Date(post.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </time>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs md:text-sm bg-[var(--border)] rounded-md px-2 py-1 text-[var(--secondary-text)]"
                          >{tag}</span>
                        ))}
                      </div>
                    </header>
                    <p className="text-base md:text-lg leading-relaxed text-[var(--secondary-text)] mb-4">
                      {post.summary}
                    </p>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="text-[var(--blue)] underline font-semibold"
                      aria-label={`Read full post: ${post.title}`}
                    >
                      Read more
                    </Link>
                  </div>
                </div>
              </article>

              {/* Subtle promo after 2nd post */}
              {idx === 0 && <SubtlePromoAd />}
            </React.Fragment>
          ))}
        </div>

        {/* LOAD MORE BUTTON */}
        {hasMore && (
          <div className="flex justify-center mt-10">
            <button
              onClick={loadMore}
              className="px-6 py-3 bg-[var(--blue)] text-white font-bold rounded-md shadow-lg"
            >
              Load More
            </button>
          </div>
        )}
      </main>
    </>
  );
}
